import { validateCard } from '@/helpers/validate-credit-card';
import { validateCNPJ } from '@/helpers/validateCnpj';
import { validateCPF } from '@/helpers/validateCpf';
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js';
import { z } from 'zod';

export const secondStep = (language: string) => {
  return z.object({
    payment_type: z.string().min(1, { message: 'Payment type is required' }),
    installments: z.string().optional(),
    card_ssn_ein: z.string().optional(),
    card_number: z.string().refine((value) => validateCard(value), {
      message: 'Invalid card number',
    }),
    card_holder: z.string().optional(),
    card_exp: z.string().min(7, { message: 'credit_card_errors.INVALID_EXPIRATION_YEAR' }),
    card_cvv: z.string().optional(),
  }).superRefine((data, ctx) => {
    if (data.payment_type !== 'credit_card') return

    if (language === 'pt-BR') {
      if (data.card_ssn_ein && data.card_ssn_ein.length < 15) {
        !validateCPF(data.card_ssn_ein.replace(/\D/g, '')) ? ctx.addIssue({
          path: ['card_ssn_ein'],
          code: z.ZodIssueCode.custom,
          message: 'Invalid SSN/EIN',
        }) : ''
      } else if (data.card_ssn_ein) {
        !validateCNPJ(data.card_ssn_ein.replace(/\D/g, '')) ? ctx.addIssue({
          path: ['card_ssn_ein'],
          code: z.ZodIssueCode.custom,
          message: 'Invalid SSN/EIN',
        }) : ''

      }
    }


    if (!data.card_ssn_ein) {
      ctx.addIssue({
        path: ['card_ssn_ein'],
        code: z.ZodIssueCode.custom,
        message: 'SSN/EIN is required for credit card payments',
      })
    }

    if (!data.installments) {
      ctx.addIssue({
        path: ['installments'],
        code: z.ZodIssueCode.custom,
        message: 'Required',
      })
    }

    if (!data.card_holder) {
      ctx.addIssue({
        path: ['car_holder'],
        code: z.ZodIssueCode.custom,
        message: 'Required',
      })
    }

    if (!data.card_cvv) {
      ctx.addIssue({
        path: ['card_cvv'],
        code: z.ZodIssueCode.custom,
        message: 'CVV is required for credit card payments',
      })
    }
  }
  )
}

export const thirdStep = (language: string) => {


  return z.object({
    full_name: z.string().optional(),
    email: z.string().email({ message: 'Invalid email address' }).min(1, {
      message: 'Required',
    }),
    ssn: z.string().optional(),
    has_foreign_doc: z.boolean().optional().default(false),
    country: z.string().min(1, { message: 'Required' }),
    state: z.string().min(1, { message: 'Required' }),
    city: z.string().min(1, { message: 'Required' }),
    street: z.string().min(1, { message: 'Required' }),
    district: z.string().optional(),
    phone_number: z.string().min(10, { message: 'Required' }).refine(
      (value) => {
        const phoneNumber = parsePhoneNumberFromString(value)

        if (phoneNumber) {
          const countryCode = phoneNumber.country
          const number = phoneNumber.number

          return isValidPhoneNumber(number, countryCode)
        }
      },
      {
        message: 'Invalid phone number',
      },
    ),
    zip_code: z.string().min(1, { message: 'Required' }),
    number: z.string().min(1, { message: 'Required' }),
    billing_type: z.string().min(1, { message: 'Required' }),
    ein: z.string().optional(),
    legal_name: z.string().optional(),
  }).superRefine((data, ctx) => {
    if (!data.has_foreign_doc) {
      if (!data.district) {
        ctx.addIssue({
          path: ['district'],
          code: z.ZodIssueCode.custom,
          message: 'Required',
        })
      }
    }

    const forbiddenCharsRegex = /[!@#$%¨*()"'\\|{}\[\]<>;]/;

    if (data.billing_type === 'individual') {
      if (!data.full_name) {
        ctx.addIssue({
          path: ['full_name'],
          code: z.ZodIssueCode.custom,
          message: 'Required',
        })
      }

      if (data.full_name && data.full_name.length < 3) {
        ctx.addIssue({
          path: ['full_name'],
          code: z.ZodIssueCode.custom,
          message: 'Min 3 characters',
        })
      }

      if (data.full_name && forbiddenCharsRegex.test(data.full_name)) {
        ctx.addIssue({
          path: ['full_name'],
          code: z.ZodIssueCode.custom,
          message: 'Invalid characters are not allowed',
        })
      }

      if (language === 'pt-BR') {
        if (!data.ssn) {
          ctx.addIssue({
            path: ['ssn'],
            code: z.ZodIssueCode.custom,
            message: 'Required',
          })
        }

        if (data.ssn && !validateCPF(data.ssn.replace(/\D/g, '')) && !data.has_foreign_doc) {
          ctx.addIssue({
            path: ['ssn'],
            code: z.ZodIssueCode.custom,
            message: 'Invalid SSN',
          })
        }
      }
    }

    if (data.billing_type === 'company') {
      if (!data.ein) {
        ctx.addIssue({
          path: ['ein'],
          code: z.ZodIssueCode.custom,
          message: 'Required',
        })
      }

      if (data.ein && !validateCNPJ(data.ein.replace(/\D/g, '')) && language === 'pt-BR') {
        ctx.addIssue({
          path: ['ein'],
          code: z.ZodIssueCode.custom,
          message: 'Invalid EIN',
        })
      }

      if (!data.legal_name) {
        ctx.addIssue({
          path: ['legal_name'],
          code: z.ZodIssueCode.custom,
          message: 'Required',
        })
      }

      if (data.legal_name && forbiddenCharsRegex.test(data.legal_name)) {
        ctx.addIssue({
          path: ['legal_name'],
          code: z.ZodIssueCode.custom,
          message: 'Invalid characters are not allowed',
        })
      }
    }
  })
}